import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Table } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { serverUrl } from '../../../serverUrl';
import './HistoryReport.scss';

const formatCurrency = (value) => `Ksh ${Number(value || 0).toLocaleString()}`;

const formatDate = (value) => {
    if (!value) {
        return '-';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return parsed.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const buildSearchString = (payment) => (
    [
        payment?.receipt_number,
        payment?.reference_number,
        payment?.payment_method,
        payment?.invoice_type_display,
        payment?.invoice_attached_display,
        payment?.approval_status,
        payment?.reconciliation_label,
        payment?.row_type,
        payment?.amount,
        payment?.date,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
);

const renderApprovalBadge = (status) => {
    if (!status) {
        return <span className="text-muted">-</span>;
    }

    const variantMap = {
        approved: 'success',
        pending: 'warning',
        rejected: 'danger',
    };

    return (
        <Badge bg={variantMap[status] || 'secondary'} className="text-capitalize">
            {status}
        </Badge>
    );
};

const renderReconciliationBadge = (status, label) => (
    <Badge bg={status ? 'success' : 'secondary'}>
        {label || (status ? 'Reconciled' : 'Not Reconciled')}
    </Badge>
);

function HousePaymentsReport({ tenantUserId, occupancyId, style }) {
    const { id } = useParams();
    const [payments, setPayments] = useState([]);
    const [summary, setSummary] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [scope, setScope] = useState('house');
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);

    const accessToken = localStorage.getItem('authTokens');
    const parsedTokens = JSON.parse(accessToken);
    const access = parsedTokens.access;

    const config = useMemo(() => ({
        headers: { Authorization: `Bearer ${access}` },
    }), [access]);

    const canUseTenantScope = Boolean(tenantUserId || occupancyId);

    useEffect(() => {
        if (!canUseTenantScope && scope !== 'house') {
            setScope('house');
        }
    }, [canUseTenantScope, scope]);

    useEffect(() => {
        let isMounted = true;
        const params = new URLSearchParams({ house_id: id });

        if (scope === 'tenant') {
            if (tenantUserId) {
                params.append('user_id', tenantUserId);
            }
            if (occupancyId) {
                params.append('occupancy_id', occupancyId);
            }
        }

        setIsLoading(true);
        serverUrl.get(`/landlord/house-payments-report/?${params.toString()}`, config)
            .then((res) => {
                if (!isMounted) {
                    return;
                }
                setPayments(Array.isArray(res.data?.results) ? res.data.results : []);
                setSummary(res.data?.summary || null);
            })
            .catch((error) => {
                console.log(error);
                if (!isMounted) {
                    return;
                }
                setPayments([]);
                setSummary(null);
                toast.error(error?.response?.data?.error || 'Unable to load house payments report.');
            })
            .finally(() => {
                if (isMounted) {
                    setIsLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [config, id, occupancyId, refreshCounter, scope, tenantUserId]);

    const filteredPayments = useMemo(() => (
        payments.filter((payment) => buildSearchString(payment).includes(searchQuery.toLowerCase()))
    ), [payments, searchQuery]);

    const showApprovalStatus = Boolean(summary?.show_approval_status);
    const itemsPerPage = 5;
    const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
    const startIndex = currentPage * itemsPerPage;
    const endIndex = currentPage === totalPages - 1 ? filteredPayments.length : startIndex + itemsPerPage;
    const displayedPayments = filteredPayments.slice(startIndex, endIndex);
    const rangeStart = filteredPayments.length === 0 ? 0 : startIndex + 1;
    const rangeEnd = filteredPayments.length === 0 ? 0 : Math.min(endIndex, filteredPayments.length);

    useEffect(() => {
        setCurrentPage(0);
    }, [searchQuery, scope, tenantUserId, occupancyId, payments.length]);

    const handlePrevPage = () => {
        setCurrentPage((current) => Math.max(current - 1, 0));
    };

    const handleNextPage = () => {
        setCurrentPage((current) => {
            if (totalPages === 0) {
                return 0;
            }
            return Math.min(current + 1, totalPages - 1);
        });
    };

    return (
        <Card
            className='selectedHouseTenantDetails mt-4'
            style={{ ...(style || {}), overflow: 'hidden' }}
        >
            <Card.Header className='d-flex flex-wrap justify-content-between align-items-center gap-3'>
                <div>
                    <div className='fw-bold'>Payments Report</div>
                    <div className='text-muted small'>
                        {scope === 'tenant' ? 'Showing payments for the selected tenant view.' : 'Showing all payments recorded for this house.'}
                    </div>
                </div>
                <div className='d-flex flex-wrap align-items-center gap-2'>
                    {canUseTenantScope && (
                        <Form.Select
                            value={scope}
                            onChange={(event) => setScope(event.target.value)}
                            style={{ minWidth: '220px' }}
                        >
                            <option value="house">All house payments</option>
                            <option value="tenant">Selected tenant only</option>
                        </Form.Select>
                    )}
                    <Form.Control
                        placeholder='Search payments'
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        style={{ minWidth: '220px' }}
                    />
                    <Button
                        variant='outline-primary'
                        onClick={() => setRefreshCounter((current) => current + 1)}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Loading...' : 'Refresh'}
                    </Button>
                </div>
            </Card.Header>
            <Card.Body style={{ height: 'auto', zoom: 1, paddingRight: '0.75rem' }}>
                <div className='d-flex flex-wrap gap-3 mb-3'>
                    <div className='border rounded px-3 py-2'>
                        <div className='text-muted small'>Payments Shown</div>
                        <div className='fw-bold'>{summary?.total_rows || 0}</div>
                    </div>
                    <div className='border rounded px-3 py-2'>
                        <div className='text-muted small'>Total Amount</div>
                        <div className='fw-bold'>{formatCurrency(summary?.total_amount || 0)}</div>
                    </div>
                    {showApprovalStatus && (
                        <div className='border rounded px-3 py-2'>
                            <div className='text-muted small'>Approval Rows</div>
                            <div className='fw-bold'>{summary?.approval_only_rows || 0}</div>
                        </div>
                    )}
                </div>

                {scope === 'tenant' && !canUseTenantScope && (
                    <Alert variant='warning' className='mb-3'>
                        Select a tenant to filter this report by occupancy.
                    </Alert>
                )}

                <div className='table-responsive' style={{ maxWidth: '100%', overflowX: 'auto', paddingRight: 0 }}>
                    <Table
                        hover
                        className='mb-0'
                        style={{ minWidth: showApprovalStatus ? '1120px' : '1000px', width: '100%' }}
                    >
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Receipt No</th>
                                <th>Reference No</th>
                                <th>Method</th>
                                <th>Invoice Type</th>
                                <th>Invoice Attached</th>
                                <th>Amount</th>
                                <th>Reconciliation</th>
                                {showApprovalStatus && <th>Approval</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {!isLoading && displayedPayments.length === 0 && (
                                <tr>
                                    <td colSpan={showApprovalStatus ? 9 : 8} className='text-center text-muted py-4'>
                                        No payments found for this house.
                                    </td>
                                </tr>
                            )}

                            {displayedPayments.map((payment) => (
                                <tr key={payment.row_key}>
                                    <td>{formatDate(payment.date)}</td>
                                    <td>{payment.receipt_number || '-'}</td>
                                    <td>{payment.reference_number || '-'}</td>
                                    <td>
                                        <div>{payment.payment_method || '-'}</div>
                                        {payment.row_type === 'approval' && (
                                            <div className='text-muted small'>Approval only</div>
                                        )}
                                    </td>
                                    <td>{payment.invoice_type_display || 'Unattached'}</td>
                                    <td>{payment.invoice_attached_display || 'Unattached'}</td>
                                    <td>{formatCurrency(payment.amount)}</td>
                                    <td>{renderReconciliationBadge(payment.reconciliation_status, payment.reconciliation_label)}</td>
                                    {showApprovalStatus && <td>{renderApprovalBadge(payment.approval_status)}</td>}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
                <div className="pagination">
                    <button onClick={handlePrevPage} disabled={currentPage === 0 || totalPages === 0}>
                        &lt;
                    </button>
                    <span>{`${rangeStart}-${rangeEnd} of ${filteredPayments.length}`}</span>
                    <button onClick={handleNextPage} disabled={totalPages === 0 || currentPage >= totalPages - 1}>
                        &gt;
                    </button>
                </div>
            </Card.Body>
        </Card>
    );
}

export default HousePaymentsReport;
